import React, { useMemo, useState, useEffect } from "react";
import useSWR from "swr";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Play, History, PowerOff, Power, Search, Info } from "lucide-react";
import { cn } from "@/lib/utils";

const fetcher = (url) => api.get(url).then((r) => r.data);

const emptyDraft = () => ({
  name: "", category: "Custom", description: "", formula: "",
  unit: "currency", trend_direction_good: "up", compare_to_target: false,
  change_note: "",
});

export default function MetricStudio() {
  const { user } = useAuth();
  const { data: metrics, mutate } = useSWR("/api/metrics", fetcher);
  const { data: schema } = useSWR("/api/metrics/schema/base-fields", fetcher);
  const { data: extSchema } = useSWR("/api/metrics/schema/extended-columns", fetcher);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // draft object
  const [preview, setPreview] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [pickerTab, setPickerTab] = useState("base"); // "base" | "sheets"
  const [pickedSheet, setPickedSheet] = useState(null);

  const canEdit = user?.role === "admin" || user?.role === "analyst";
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (metrics && !selectedId && metrics.length) setSelectedId(metrics[0].metric_id);
  }, [metrics, selectedId]);

  const selected = useMemo(
    () => metrics?.find((m) => m.metric_id === selectedId),
    [metrics, selectedId]
  );

  useEffect(() => {
    if (selected) setEditing({
      name: selected.name, category: selected.category, description: selected.description || "",
      formula: selected.formula, unit: selected.unit, trend_direction_good: selected.trend_direction_good,
      compare_to_target: !!selected.compare_to_target, change_note: "",
      period_aggregation: selected.period_aggregation || "period_total",
      recompute_history: false,
    });
    setPreview(null);
  }, [selected]);

  const filtered = useMemo(() => {
    if (!metrics) return [];
    const q = search.toLowerCase();
    return metrics.filter((m) =>
      m.name.toLowerCase().includes(q) ||
      m.category.toLowerCase().includes(q) ||
      m.formula.toLowerCase().includes(q)
    );
  }, [metrics, search]);

  const runPreview = async (formula) => {
    try {
      const { data } = await api.post("/api/metrics/preview", { formula, months: 3 });
      setPreview(data.preview || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setPreview(null);
    }
  };

  const saveEdit = async () => {
    if (!selected) return;
    if (!editing.change_note.trim()) {
      toast.error("Add a change note before saving");
      return;
    }
    try {
      await api.patch(`/api/metrics/${selected.metric_id}`, editing);
      toast.success("Metric saved");
      mutate();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const setStatus = async (status) => {
    if (!selected) return;
    try {
      await api.patch(`/api/metrics/${selected.metric_id}`, {
        status, change_note: `Status set to ${status}`,
      });
      toast.success(`Status: ${status}`);
      mutate();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const insertField = (f) => {
    if (!editing) return;
    setEditing({ ...editing, formula: (editing.formula || "") + f });
  };

  const insertColRef = (fn, sheet, column) => {
    if (!editing) return;
    const safeSheet = sheet.replace(/"/g, '\\"');
    const safeCol = column.replace(/"/g, '\\"');
    const snippet = `${fn}("${safeSheet}", "${safeCol}")`;
    setEditing({ ...editing, formula: (editing.formula || "") + snippet });
  };

  const extSheets = extSchema?.sheets || [];

  return (
    <div className="p-6 md:p-8">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-stone-500">
            Metric Studio
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tighter text-stone-900 mt-1">
            Formulas, not features.
          </h1>
        </div>
        {canEdit && (
          <AddMetricDialog open={addOpen} setOpen={setAddOpen} schema={schema} onCreated={() => mutate()} isAdmin={isAdmin} />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        {/* Left: list */}
        <div className="rounded-lg border border-stone-200 bg-white overflow-hidden">
          <div className="p-3 border-b border-stone-200">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-stone-400" />
              <Input
                placeholder="Search metrics"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="metric-search-input"
                className="pl-8 h-9 border-stone-300"
              />
            </div>
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {filtered.map((m) => (
              <button
                key={m.metric_id}
                onClick={() => setSelectedId(m.metric_id)}
                data-testid={`metric-item-${m.metric_id}`}
                className={cn(
                  "w-full text-left px-4 py-3 border-b border-stone-100 hover:bg-stone-50 transition-colors",
                  selectedId === m.metric_id && "bg-stone-100",
                  m.status === "deactivated" && "opacity-50"
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium text-sm text-stone-900 truncate">{m.name}</div>
                  <Badge
                    variant="outline"
                    className={cn(
                      "text-[9px] font-mono uppercase tracking-wide",
                      m.status === "active" && "border-brand-good text-brand-good",
                      m.status === "pending_approval" && "border-yellow-600 text-yellow-700",
                      m.status === "deactivated" && "border-stone-400 text-stone-500",
                    )}
                  >
                    {m.status}
                  </Badge>
                </div>
                <div className="text-[10px] font-mono text-stone-500 mt-1 truncate">{m.category}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Right: editor */}
        {selected && editing && (
          <div className="space-y-6" data-testid="metric-editor">
            <div className="rounded-lg border border-stone-200 bg-white p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">
                    {selected.category} · v{selected.version} · {selected.status}
                  </div>
                  <h2 className="font-heading text-2xl font-medium tracking-tight text-stone-900 mt-1">
                    {selected.name}
                  </h2>
                </div>
                {isAdmin && (
                  <div className="flex gap-2">
                    {selected.status !== "deactivated" ? (
                      <Button variant="outline" size="sm" onClick={() => setStatus("deactivated")} data-testid="metric-deactivate-btn">
                        <PowerOff className="w-3.5 h-3.5 mr-1.5" /> Deactivate
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => setStatus("active")} data-testid="metric-activate-btn">
                        <Power className="w-3.5 h-3.5 mr-1.5" /> Activate
                      </Button>
                    )}
                    {selected.status === "pending_approval" && (
                      <Button size="sm" className="bg-brand-good text-white" onClick={() => setStatus("active")} data-testid="metric-approve-btn">
                        Approve
                      </Button>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">Name</Label>
                  <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1 border-stone-300" data-testid="metric-name-input" />
                </div>
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">Category</Label>
                  <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                    <SelectTrigger className="mt-1 border-stone-300" data-testid="metric-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(schema?.categories || []).map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">Unit</Label>
                  <Select value={editing.unit} onValueChange={(v) => setEditing({ ...editing, unit: v })}>
                    <SelectTrigger className="mt-1 border-stone-300" data-testid="metric-unit-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(schema?.units || []).map((u) => (
                        <SelectItem key={u} value={u}>{u}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">Trend direction good</Label>
                  <Select value={editing.trend_direction_good} onValueChange={(v) => setEditing({ ...editing, trend_direction_good: v })}>
                    <SelectTrigger className="mt-1 border-stone-300" data-testid="metric-trend-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="up">Up is good</SelectItem>
                      <SelectItem value="down">Down is good</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">For Quarter / YTD views</Label>
                  <Select value={editing.period_aggregation} onValueChange={(v) => setEditing({ ...editing, period_aggregation: v })}>
                    <SelectTrigger className="mt-1 border-stone-300" data-testid="metric-period-aggregation-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="period_total">Total for the period — combine the months, then apply the formula</SelectItem>
                      <SelectItem value="monthly_average">Average per month — apply the formula each month, then average</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">Description</Label>
                  <Textarea rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="mt-1 border-stone-300" data-testid="metric-description-input" />
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={editing.compare_to_target} onCheckedChange={(v) => setEditing({ ...editing, compare_to_target: v })} data-testid="metric-target-switch" />
                  <Label className="text-sm text-stone-700">Compare to target</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={!!editing.recompute_history} onCheckedChange={(v) => setEditing({ ...editing, recompute_history: v })} data-testid="metric-recompute-switch" />
                  <Label className="text-sm text-stone-700">Recompute history on save</Label>
                </div>
              </div>
            </div>

            {/* Formula builder */}
            <div className="rounded-lg border border-stone-200 bg-white overflow-hidden">
              <div className="px-6 py-4 border-b border-stone-200">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">Formula</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_240px]">
                <div className="p-6">
                  <Textarea
                    rows={5}
                    value={editing.formula}
                    onChange={(e) => setEditing({ ...editing, formula: e.target.value })}
                    className="font-mono text-sm bg-[#1F2A22] text-[#E7EDE9] border-0 focus-visible:ring-1 focus-visible:ring-brand"
                    data-testid="metric-formula-input"
                  />
                  <div className="flex flex-wrap gap-2 mt-3">
                    {["+", "-", "*", "/", "(", ")", "safe_div(a, b)"].map((op) => (
                      <button
                        key={op}
                        type="button"
                        onClick={() => insertField(op)}
                        className="px-2.5 py-1 rounded border border-stone-300 bg-white hover:bg-stone-50 font-mono text-xs"
                        data-testid={`op-btn-${op}`}
                      >
                        {op}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button
                      onClick={() => runPreview(editing.formula)}
                      className="bg-brand hover:bg-brand-hover text-white"
                      data-testid="metric-preview-btn"
                    >
                      <Play className="w-3.5 h-3.5 mr-1.5" /> Preview
                    </Button>
                    <div className="flex-1" />
                    <Input
                      placeholder="Change note (required)"
                      value={editing.change_note}
                      onChange={(e) => setEditing({ ...editing, change_note: e.target.value })}
                      className="max-w-xs border-stone-300"
                      data-testid="metric-change-note-input"
                    />
                    <Button variant="default" className="bg-stone-900 text-white hover:bg-stone-800" onClick={saveEdit} data-testid="metric-save-btn">
                      Save
                    </Button>
                  </div>

                  {preview && (
                    <div className="mt-6" data-testid="metric-preview-panel">
                      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">
                        Live preview (last 3 months)
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        {preview.map((p) => (
                          <div key={p.month} className={cn(
                            "rounded-md border p-3",
                            p.ok ? "border-stone-200 bg-stone-50" : "border-red-200 bg-red-50"
                          )}>
                            <div className="text-[10px] font-mono text-stone-500">{p.month}</div>
                            <div className={cn("font-mono text-lg font-medium mt-1", !p.ok && "text-red-700")}>
                              {p.ok ? (p.value?.toFixed?.(2) ?? "—") : "error"}
                            </div>
                            {!p.ok && <div className="text-[10px] font-mono text-red-700 mt-1">{p.error}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Field picker */}
                <div className="border-t md:border-t-0 md:border-l border-stone-200 bg-stone-50 p-4 max-h-[520px] overflow-y-auto">
                  <div className="flex items-center justify-between mb-3">
                    <div className="inline-flex rounded-md border border-stone-200 bg-white overflow-hidden" data-testid="picker-tabs">
                      <button
                        type="button"
                        onClick={() => setPickerTab("base")}
                        data-testid="picker-tab-base"
                        className={cn(
                          "px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em]",
                          pickerTab === "base" ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-50"
                        )}
                      >
                        Base fields
                      </button>
                      <button
                        type="button"
                        onClick={() => setPickerTab("sheets")}
                        data-testid="picker-tab-sheets"
                        className={cn(
                          "px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] border-l border-stone-200",
                          pickerTab === "sheets" ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-50"
                        )}
                      >
                        By sheet
                      </button>
                    </div>
                    {pickerTab === "base" && <FieldReferenceDialog lineage={schema?.lineage} />}
                  </div>

                  {pickerTab === "base" && (
                    <>
                      <div className="flex flex-wrap gap-1.5">
                        {(schema?.base_fields || []).map((f) => {
                          const l = schema?.lineage?.[f];
                          const title = l ? `Source: ${l.sheet} → ${l.column}${l.notes ? "\n" + l.notes : ""}` : f;
                          return (
                            <button
                              key={f}
                              onClick={() => insertField(f)}
                              title={title}
                              className="px-2 py-1 rounded bg-white border border-stone-200 hover:border-brand hover:text-brand font-mono text-[10px]"
                              data-testid={`field-chip-${f}`}
                            >
                              {f}
                            </button>
                          );
                        })}
                      </div>
                      <div className="mt-4 text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">
                        Suffix helpers
                      </div>
                      <div className="text-[11px] font-mono text-stone-600">
                        <span className="block">field<span className="text-brand">_current</span></span>
                        <span className="block">field<span className="text-brand">_prior</span></span>
                      </div>
                      <div className="mt-4 text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">
                        Functions
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {(schema?.functions || []).map((f) => (
                          <button
                            key={f}
                            onClick={() => insertField(`${f}()`)}
                            className="px-2 py-1 rounded bg-white border border-stone-200 hover:border-brand hover:text-brand font-mono text-[10px]"
                            data-testid={`fn-chip-${f}`}
                          >
                            {f}
                          </button>
                        ))}
                      </div>
                    </>
                  )}

                  {pickerTab === "sheets" && (
                    <div data-testid="picker-sheets-panel">
                      {extSheets.length === 0 ? (
                        <div className="text-xs text-stone-500 leading-relaxed">
                          No sheet columns discovered yet. Upload a MIS pack from the Uploads page — the parser scans every numeric column across all sheets and lists them here for direct use in formulas.
                        </div>
                      ) : (
                        <>
                          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">Pick a sheet</div>
                          <div className="flex flex-wrap gap-1.5 mb-3">
                            {extSheets.map((s) => (
                              <button
                                key={s.sheet}
                                onClick={() => setPickedSheet(pickedSheet === s.sheet ? null : s.sheet)}
                                data-testid={`sheet-chip-${s.sheet}`}
                                className={cn(
                                  "px-2 py-1 rounded border font-mono text-[10px]",
                                  pickedSheet === s.sheet
                                    ? "bg-brand text-white border-brand"
                                    : "bg-white border-stone-200 hover:border-brand hover:text-brand"
                                )}
                              >
                                {s.sheet}
                              </button>
                            ))}
                          </div>
                          {pickedSheet && (
                            <>
                              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">
                                Columns · click to insert <span className="font-mono normal-case text-stone-400">col(&quot;sheet&quot;, &quot;column&quot;)</span>
                              </div>
                              <div className="flex flex-wrap gap-1.5 mb-3">
                                {(extSheets.find((s) => s.sheet === pickedSheet)?.columns || []).map((c) => (
                                  <button
                                    key={c}
                                    onClick={() => insertColRef("col", pickedSheet, c)}
                                    title={`Insert col("${pickedSheet}", "${c}")`}
                                    data-testid={`col-chip-${pickedSheet}-${c}`}
                                    className="px-2 py-1 rounded bg-white border border-stone-200 hover:border-brand hover:text-brand font-mono text-[10px]"
                                  >
                                    {c}
                                  </button>
                                ))}
                              </div>
                              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">
                                Aggregation variants
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {["col", "col_prior", "col_avg", "col_count"].map((fn) => (
                                  <button
                                    key={fn}
                                    onClick={() => insertField(`${fn}("${pickedSheet}", "")`)}
                                    data-testid={`col-fn-${fn}`}
                                    className="px-2 py-1 rounded bg-white border border-stone-200 hover:border-brand hover:text-brand font-mono text-[10px]"
                                  >
                                    {fn}()
                                  </button>
                                ))}
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* History */}
            <div className="rounded-lg border border-stone-200 bg-white p-6" data-testid="metric-history">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-3 flex items-center gap-2">
                <History className="w-3.5 h-3.5" /> Change history
              </div>
              <div className="space-y-2 text-sm">
                {(selected.change_history || []).slice().reverse().map((h, i) => (
                  <div key={i} className="border border-stone-100 rounded p-3">
                    <div className="text-[10px] font-mono text-stone-500">
                      v{h.version} · {h.changed_by} · {h.changed_at?.slice(0, 19).replace("T", " ")}
                    </div>
                    <div className="font-mono text-xs mt-1 text-stone-900">
                      {h.previous_formula && (
                        <div className="text-red-600 line-through">− {h.previous_formula}</div>
                      )}
                      <div className="text-green-700">+ {h.formula}</div>
                    </div>
                    {h.note && <div className="text-xs text-stone-500 mt-2">Note: {h.note}</div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FieldReferenceDialog({ lineage }) {
  const rows = React.useMemo(() => {
    if (!lineage) return [];
    return Object.entries(lineage).map(([field, meta]) => ({ field, ...meta }));
  }, [lineage]);
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-[10px] font-medium text-brand hover:underline"
          data-testid="field-reference-btn"
        >
          <Info className="w-3 h-3" /> Field reference
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Base-field lineage</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-stone-600 mb-2">
          Each base field below is computed by the parser from a specific sheet + column in
          your uploaded MIS workbook. Hover a chip in the field picker for the same info inline.
        </div>
        <div className="overflow-y-auto flex-1 -mx-6 px-6">
          <table className="w-full text-sm" data-testid="field-reference-table">
            <thead className="sticky top-0 bg-white">
              <tr className="text-left text-[10px] font-bold uppercase tracking-[0.15em] text-stone-500 border-b border-stone-200">
                <th className="py-2 pr-3">Base field</th>
                <th className="py-2 pr-3">Source sheet</th>
                <th className="py-2 pr-3">Column / Derivation</th>
                <th className="py-2 pr-3">Notes</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.field} className="border-b border-stone-100 last:border-0" data-testid={`lineage-row-${r.field}`}>
                  <td className="py-2 pr-3 font-mono text-xs text-brand font-medium">{r.field}</td>
                  <td className="py-2 pr-3 text-xs">{r.sheet}</td>
                  <td className="py-2 pr-3 text-xs">{r.column}</td>
                  <td className="py-2 pr-3 text-xs text-stone-500">{r.notes || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddMetricDialog({ open, setOpen, schema, onCreated, isAdmin }) {
  const [draft, setDraft] = useState(emptyDraft());
  const submit = async () => {
    try {
      await api.post("/api/metrics", draft);
      toast.success(isAdmin ? "Metric created" : "Submitted for approval");
      setDraft(emptyDraft());
      setOpen(false);
      onCreated?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-brand hover:bg-brand-hover text-white" data-testid="metric-add-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add metric
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New metric</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="add-name" />
          <div className="grid grid-cols-2 gap-3">
            <Select value={draft.category} onValueChange={(v) => setDraft({ ...draft, category: v })}>
              <SelectTrigger data-testid="add-category"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                {(schema?.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={draft.unit} onValueChange={(v) => setDraft({ ...draft, unit: v })}>
              <SelectTrigger data-testid="add-unit"><SelectValue placeholder="Unit" /></SelectTrigger>
              <SelectContent>
                {(schema?.units || []).map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Select value={draft.trend_direction_good} onValueChange={(v) => setDraft({ ...draft, trend_direction_good: v })}>
            <SelectTrigger data-testid="add-trend"><SelectValue placeholder="Trend direction good" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="up">Up is good</SelectItem>
              <SelectItem value="down">Down is good</SelectItem>
            </SelectContent>
          </Select>
          <Textarea rows={2} placeholder="Description" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} data-testid="add-description" />
          <Textarea rows={3} placeholder="Formula (e.g. revenue_net / invoice_count)" className="font-mono bg-stone-50" value={draft.formula} onChange={(e) => setDraft({ ...draft, formula: e.target.value })} data-testid="add-formula" />
          <Input placeholder="Change note" value={draft.change_note} onChange={(e) => setDraft({ ...draft, change_note: e.target.value })} data-testid="add-change-note" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="bg-brand hover:bg-brand-hover text-white" onClick={submit} data-testid="add-submit">
            {isAdmin ? "Create" : "Submit for approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
